// src/pages/Templates/TemplatesListPage.tsx

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom"; // <-- Import useNavigate
import {
  Plus,
  Edit,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
} from "lucide-react";
import { toast } from "react-hot-toast";
import ConfirmationDialog from "../../widgets/ConfirmationModal";
import templateService, { Template, TemplateListParams } from "../../services/template.service";
import Loader from "../../widgets/Loader";
import Tooltip from "../../widgets/ToolTip";
import Can from "../../components/auth/Can";

function Templates() {
  const navigate = useNavigate(); // <-- Initialize useNavigate
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [searchFilters, setSearchFilters] = useState({
    schema_name: "",
  });

  const isInitialMount = useRef(true);

  useEffect(() => {
    // Debounced search logic remains the same
    if (isInitialMount.current) {
      fetchTemplates();
      isInitialMount.current = false;
    } else if (Object.values(searchFilters).some((filter) => filter !== "")) {
      const debounceTimer = setTimeout(() => {
        fetchTemplates();
      }, 500);
      return () => clearTimeout(debounceTimer);
    } else {
      fetchTemplates();
    }
  }, [currentPage, searchFilters]);

  const fetchTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      const params: TemplateListParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(searchFilters.schema_name && { schema_name: searchFilters.schema_name }),
      };

      const response = await templateService.getTemplates(params);
      setTemplates(response?.data?.schemaConfigsList || []);
      setTotalCount(response?.data?.total_count || 0);
    } catch (error: any) {
       // Error handling remains the same
       if (error.response?.data?.error?.error_description) {
        toast.error(error.response.data.error.error_description);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to fetch templates");
      }
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // --- Navigation Handlers ---
  const handleAddTemplate = () => {
    navigate("/templates/new"); // <-- Navigate to the create form page
  };

  const handleEditTemplate = (template: Template) => {
    navigate(`/templates/edit/${template._id}`); // <-- Navigate to the edit form page with ID
  };
  
  // --- Delete Logic (remains the same) ---
  const handleDeleteClick = (template: Template) => {
    setTemplateToDelete(template);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (templateToDelete) {
      try {
        await templateService.deleteTemplate(templateToDelete._id);
        toast.success(`Template deleted successfully!`);
        await fetchTemplates(); // Refresh list
      } catch (error: any) {
        // Error handling for delete
        if (error?.response?.data?.error?.error_description) {
          toast.error(error?.response?.data?.error?.error_description);
        } else if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        } else {
          toast.error("Failed to delete template");
        }
      } finally {
        setTemplateToDelete(null);
        setShowDeleteConfirm(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setTemplateToDelete(null);
    setShowDeleteConfirm(false);
  };
  
  // --- Search and Pagination (remains the same) ---
  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const handlePreviewClick = (template: Template) => {
    console.log(template,'hii');
    
   navigate(`/templates/preview/${template._id}`);
  }

  // --- JSX for the list page ---
  return (
    <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
      <div className="px-6 py-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-lg font-bold text-slate-900">Templates</h1>
            <p className="text-xs text-slate-600">Manage your document templates</p>
          </div>
          <div className="flex gap-2 items-center justify-end">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by template name..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                value={searchFilters?.schema_name || ""}
                onChange={(e) => handleSearchChange("schema_name", e.target.value)}
              />
            </div>

            <Can permission="templates:create">
            <button
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              onClick={handleAddTemplate}
              type="button"
            >
              <Plus className="w-4 h-4" />
              Add Template
            </button>
            </Can>
          </div>
        </div>
        </div>
        </div>

        {/* Templates Table and Pagination (JSX remains the same) */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* ... table, pagination JSX from your original component ... */}
           <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Templates List</h3>
            <div className="text-sm text-gray-500">
              {totalCount > 0 && templates.length > 1 &&
                `Showing ${startItem}-${endItem} of ${totalCount} templates`}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoadingTemplates ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      <Loader />
                    </td>
                  </tr>
                ) : templates.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      No templates found. Click "Add Template" to create your first template.
                    </td>
                  </tr>
                ) : (
                  templates.map((template: any) => (
                    <tr key={template._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{template?.schema_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{template?.schema_description || "--"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(template?.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <Can permission="templates:preview">
                          <Tooltip content="Preview">
                            <button onClick={() => handlePreviewClick(template)} className="text-green-600 hover:text-green-900 p-1 rounded" type="button"><Eye className="w-4 h-4" /></button>
                            </Tooltip>
                          </Can>
                          <Can permission="templates:edit">
                          <Tooltip content="Edit">
                            <button onClick={() => handleEditTemplate(template)} className="text-blue-600 hover:text-blue-900 p-1 rounded" type="button"><Edit className="w-4 h-4" /></button>
                          </Tooltip>
                          </Can>
                          <Can permission="templates:delete">
                          <Tooltip content="Delete">
                            <button onClick={() => handleDeleteClick(template)} className="text-red-600 hover:text-red-900 p-1 rounded"><Trash2 className="w-4 h-4" /></button>
                          </Tooltip>
                          </Can>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {/* Showing <span className="font-medium">{startItem}</span> to{" "}
                <span className="font-medium">{endItem}</span> of{" "}
                <span className="font-medium">{totalCount}</span> results */}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

        </div>
      
      {/* Confirmation Modal for Delete */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Template"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        description={`Are you sure you want to delete the template <strong>${templateToDelete?.schema_name}</strong>? This action cannot be undone.`}
        iconName="warning"
      />
    </div>
  );
}

export default Templates;
